import React, { useState, useMemo } from 'react';
import { GradientModal } from './';
import { ALL_PLANT_ICONS, getIconSrc } from '../../utils';

/**
 * IconPickerModal - Modal for selecting a plant icon from all available icons.
 * Shows a scrollable grid of plant icons at 40×40, with search filtering.
 *
 * @param {Object} props
 * @param {boolean} props.isOpen - Whether the modal is open
 * @param {Function} props.onClose - Callback to close the modal
 * @param {Function} props.onSelect - Callback when icon is selected (iconName: string)
 * @param {string|null} [props.currentIcon] - Currently selected icon name (for highlighting)
 */
const IconPickerModal = ({ isOpen, onClose, onSelect, currentIcon }) => {
  const [search, setSearch] = useState('');

  const filteredIcons = useMemo(() => {
    if (!search.trim()) return ALL_PLANT_ICONS;
    const term = search.trim().toLowerCase();
    return ALL_PLANT_ICONS.filter(name =>
      name.toLowerCase().includes(term)
    );
  }, [search]);

  const handleSelect = (iconName) => {
    onSelect(iconName);
    setSearch('');
    onClose();
  };

  const handleClose = () => {
    setSearch('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <GradientModal
      isOpen={isOpen}
      onClose={handleClose}
      title="Choose Icon"
      subtitle="Select an icon below. This will update the icon everywhere this plant appears."
      maxWidth="max-w-lg"
      zIndex="z-[60]"
    >
      {/* Search input */}
      <input
        type="text"
        value={search}
        onChange={(e) => setSearch(e.target.value)}
        placeholder="Search icons..."
        className="w-full px-4 py-2.5 border-2 border-gray-200 rounded-xl focus:border-[#52A675] focus:ring-0 focus:outline-none transition-colors text-[#2F5233] bg-white mb-4 text-base"
        autoFocus
      />

      {/* Tap to select hint */}
      <p className="text-xs text-gray-500 mb-3">Tap an icon to select it</p>

      {/* Icon grid */}
      <div className="grid grid-cols-4 sm:grid-cols-5 gap-3 max-h-80 overflow-y-auto p-1">
        {filteredIcons.map(iconName => {
          const src = getIconSrc(iconName);
          const isSelected = currentIcon === iconName;
          return (
            <button
              key={iconName}
              type="button"
              onClick={() => handleSelect(iconName)}
              className={`flex items-center justify-center p-2 rounded-xl border-2 transition-all ${
                isSelected
                  ? 'bg-[#C7F464] border-[#52A675] ring-2 ring-[#52A675]'
                  : 'border-transparent hover:bg-[#52A675]/10 hover:border-[#52A675]/30'
              }`}
              title={iconName.replace(/-/g, ' ')}
            >
              {src && (
                <img
                  src={src}
                  alt={iconName}
                  width={72}
                  height={72}
                  draggable={false}
                  onError={(e) => { e.target.style.visibility = 'hidden'; }}
                />
              )}
            </button>
          );
        })}
      </div>

      {filteredIcons.length === 0 && (
        <p className="text-center text-gray-400 text-sm py-4">No icons match "{search}"</p>
      )}

      {/* Cancel button - full width, single button standard */}
      <button
        onClick={handleClose}
        className="w-full mt-4 px-4 py-3 rounded-xl bg-gray-200 hover:bg-gray-300 text-[#2F5233] font-semibold transition-colors"
      >
        Cancel
      </button>
    </GradientModal>
  );
};

export default IconPickerModal;
